<script lang="ts">
// Module-level refcount shared by every StickyContainer instance: how many
// mounted fix-to-page instances currently need `body { position: relative }`.
// Without it, unmounting one instance would clear the body positioning out
// from under another still-active one.
let fixToPageHolders = 0
</script>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useAttrs } from 'vue'

import { useDirection } from '@render/composables/useDirection'
import { useStickyBoundsIfAvailable } from '@render/composables/useStickyBounds'
import { captureAnimationPhases, restoreAnimationPhases } from '@render/lib/animationPhase'
import { getSizeStyleData, getSpacingStyleData } from '@render/composables/useStyleData'
import { effectiveZoom, viewportRect } from '@render/lib/pageZoom'
import type { StickyContainerFields as Model, StickyMode, StickyPosition } from './definition'
import { OVERLAY_HEIGHT_VAR, OVERLAY_WIDTH_VAR } from './prerenderedOverlay'

const fields = defineModel<Model>('fields', {
  default: () => ({
    mode: 'sticky' as StickyMode,
    position: 'top' as StickyPosition,
  }),
})

// The overlay modes move their content out of the component tree, so the
// fall-through `data-id` stays behind on a 0-height placeholder. Re-stamping the
// owning nodeId onto the teleported half is what lets the editor's preview tools
// find this component's real box: without it a screenshot of it comes back as
// ZERO_SIZE and its children are reported as top-level page content. Read off
// `useAttrs` rather than declared as a prop, mirroring `useComponentProcessors`.
const attrs = useAttrs()
const teleportOwner = computed(() => (attrs['data-id'] as string | undefined) ?? undefined)

const mode = computed<StickyMode>(() => fields.value.mode ?? 'sticky')
const position = computed<StickyPosition>(() => fields.value.position ?? 'top')
const zIndex = computed(() => fields.value.zIndex ?? 9999)
const offsetCss = computed(() => getSizeStyleData(fields.value.offset, '0px') ?? '0px')
const devHeadingPadding = computed(() => getSpacingStyleData(fields.value.padding))
const { isRtl } = useDirection()

// Same row/column/block layout family as Container/ColumnsOrRows, applied to
// whichever element wraps the slot for the active mode (`.stick-native` or
// `.stick-content`). Kebab-case keys so this object works unmodified both as
// a Vue `:style` binding (Vue camelizes on assignment) and spread into the
// `applyContentStyles` map below, which writes via `setProperty` and needs
// kebab-case CSS property names.
const layoutStyle = computed<Record<string, string>>(() => {
  const layout = fields.value.layout || 'block'
  if (layout === 'block') return {}
  const s: Record<string, string> = {
    display: 'flex',
    'flex-direction': layout,
  }
  const gap = getSpacingStyleData(fields.value.gap)
  if (gap) s.gap = gap
  if (fields.value.alignItems) s['align-items'] = fields.value.alignItems
  if (fields.value.justifyContent) s['justify-content'] = fields.value.justifyContent
  if (fields.value.wrap) s['flex-wrap'] = 'wrap'
  return s
})

// Explicit sizing, same fields as Container/StackedContainer. Deliberately
// NOT applied in the 'fullscreen' branch of recalc() below — that mode's
// width/height are the viewport-fill invariant the component exists for, so
// user sizing only reaches native sticky and the fix-to-* overlay content.
const sizeStyle = computed<Record<string, string>>(() => {
  const s: Record<string, string> = {}
  const width = getSizeStyleData(fields.value.width)
  if (width) s.width = width
  const minWidth = getSizeStyleData(fields.value.minWidth)
  if (minWidth) s['min-width'] = minWidth
  const maxWidth = getSizeStyleData(fields.value.maxWidth)
  if (maxWidth) s['max-width'] = maxWidth
  const height = getSizeStyleData(fields.value.height)
  if (height) s.height = height
  const minHeight = getSizeStyleData(fields.value.minHeight)
  if (minHeight) s['min-height'] = minHeight
  const maxHeight = getSizeStyleData(fields.value.maxHeight)
  if (maxHeight) s['max-height'] = maxHeight
  return s
})

const toCssText = (style: Record<string, string>) =>
  Object.entries(style)
    .map(([prop, value]) => `${prop}:${value}`)
    .join(';')

const prerenderedOverlayStyles = ():
  | { placeholderHeight: string; content: string; inner?: string }
  | undefined => {
  if (mode.value === 'fix-to-screen') {
    return {
      placeholderHeight: fields.value.reserveSpace ? `var(${OVERLAY_HEIGHT_VAR}, 0px)` : '0px',
      content: toCssText({
        position: 'fixed',
        [position.value]: offsetCss.value,
        width: `var(${OVERLAY_WIDTH_VAR}, 100%)`,
        'z-index': String(zIndex.value),
        ...layoutStyle.value,
        ...sizeStyle.value,
      }),
    }
  }
  if (mode.value === 'fullscreen') {
    return {
      placeholderHeight: '0px',
      content: [
        'position:fixed',
        'top:0',
        'left:0',
        'width:calc(100vw / var(--app-zoom, 1))',
        'height:calc(100vh / var(--app-zoom, 1))',
        'height:calc(100dvh / var(--app-zoom, 1))',
        'box-sizing:border-box',
        `z-index:${zIndex.value}`,
      ].join(';'),
      inner: toCssText({
        display: 'flow-root',
        width: '100%',
        height: '100%',
        'box-sizing': 'border-box',
        'overflow-x': 'hidden',
        'overflow-y': 'auto',
        'overscroll-behavior': 'contain',
        ...layoutStyle.value,
      }),
    }
  }
  return undefined
}

const prerendered = import.meta.env.SSR ? prerenderedOverlayStyles() : undefined

// Nearest ancestor providing sticky bounds (e.g. SplitBackground's own
// top/bottom section), if any — see useStickyBounds.ts.
const stickyBounds = useStickyBoundsIfAvailable()
const boundsEl = computed(() => stickyBounds?.value ?? null)

// 'fix-to-bounds' with no provided ancestor has nothing to clamp to — degrade
// to 'fix-to-page' rather than silently teleporting to an element that
// doesn't exist. Every mode-driven branch below reads `effectiveMode`, not
// the raw field, so the fallback is applied consistently everywhere.
const effectiveMode = computed<StickyMode>(() =>
  mode.value === 'fix-to-bounds' && !boundsEl.value ? 'fix-to-page' : mode.value,
)
// Vue's Teleport `to` accepts an Element directly, not just a selector — so
// 'fix-to-bounds' teleports straight into boundsEl rather than body, keeping
// it a real descendant of the section it needs to clamp to.
const teleportTarget = computed<string | HTMLElement>(() =>
  effectiveMode.value === 'fix-to-bounds' ? (boundsEl.value as HTMLElement) : 'body',
)
const isNativeSticky = computed(() => mode.value === 'sticky')
const nativeStickyStyle = computed(() => ({
  [position.value]: offsetCss.value,
  zIndex: String(zIndex.value),
  ...layoutStyle.value,
  ...sizeStyle.value,
}))

// ---------------------------------------------------------------------------
// Overlay modes ('fix-to-screen' | 'fix-to-page' | 'fullscreen') — the content
// is always out of flow, teleported to body so ancestor transforms can't
// hijack the fixed/absolute containing block. The placeholder collapses to 0
// height (unless reserveSpace) and only marks where the container naturally
// sits, so the overlay can mirror its horizontal position/width.
//
// All geometry is written imperatively to the teleported element, diffed
// against what was last applied — scroll/resize never touch reactive state,
// so the slotted children are never re-rendered by repositioning.
// ---------------------------------------------------------------------------
const placeholderRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)
// Wraps the slot content one level inside `.stick-content`. For every mode but
// 'fullscreen' this generates no box at all (`display: contents`, see its
// scoped CSS) — `.stick-content` itself already carries the design-width scale
// compensation below. 'fullscreen' is the odd one out: its own box must stay
// real-viewport px (`100vw`/`100dvh`, ignoring ancestor zoom on purpose), so
// this inner ref is where THAT mode applies the same compensation instead,
// which needs a real box to hold the `transform: scale()`.
const innerRef = ref<HTMLElement | null>(null)
const offsetMeasureRef = ref<HTMLElement | null>(null)

// Teleport gate — flips once per overlay activation, right after the first
// positioning pass, so the content never paints un-positioned at body level.
const overlayMounted = ref(false)
const placeholderHeight = ref(prerendered?.placeholderHeight ?? '0px')

const diffApplyStyles = (
  el: HTMLElement | null,
  prev: Record<string, string>,
  next: Record<string, string>,
): Record<string, string> => {
  if (!el) return prev
  for (const prop of Object.keys(prev)) {
    if (!(prop in next)) el.style.removeProperty(prop)
  }
  for (const [prop, value] of Object.entries(next)) {
    if (prev[prop] !== value) el.style.setProperty(prop, value)
  }
  return next
}

let appliedStyles: Record<string, string> = {}
const applyContentStyles = (next: Record<string, string>) => {
  appliedStyles = diffApplyStyles(contentRef.value, appliedStyles, next)
}

let appliedInnerStyles: Record<string, string> = {}
const applyInnerStyles = (next: Record<string, string>) => {
  appliedInnerStyles = diffApplyStyles(innerRef.value, appliedInnerStyles, next)
}

let overlayActive = false

const recalc = () => {
  if (!overlayActive) return
  const placeholder = placeholderRef.value
  const content = contentRef.value
  if (!placeholder || !content) return

  // Ghost element keeps space in the flow when asked (the user must place the
  // StickyContainer at the end of the page content for bottom-anchoring).
  placeholderHeight.value = fields.value.reserveSpace ? `${content.offsetHeight}px` : '0px'

  // viewportRect returns the actual visually SCALED pixels while offsetWidth
  // is unscaled layout pixels — their ratio is the ancestor zoom (body > #app's
  // `zoom: var(--app-zoom)`, see DEFAULT_BASE_CSS). Not a bare
  // getBoundingClientRect: pre-standard zoom engines report that in the
  // element's own px, which would read the ratio as 1 (see pageZoom.ts).
  // Computed unconditionally, including for 'fullscreen': that mode's own box
  // is deliberately NOT scaled (below), but its slot content is still authored
  // assuming #app's design-width scale, and teleporting out of #app escapes
  // that scale the same as every other mode.
  const placeholderRect = viewportRect(placeholder)
  const offsetWidth = placeholder.offsetWidth
  if (!offsetWidth) return
  const scale = placeholderRect.width / offsetWidth

  // Only emit a `transform: scale()` when the ancestor zoom is meaningfully
  // off unity. At/near design width the zoom is ~1, so the transform would be
  // a visual no-op — but `position: fixed` + `transform` still forces iOS
  // WebKit to promote the teleported overlay to a composited layer whose
  // touch hit-region de-syncs from its painted box, swallowing taps meant for
  // other content. Skipping the no-op transform avoids that layer entirely.
  const needsScale = Math.abs(scale - 1) > 0.005

  if (effectiveMode.value === 'fullscreen') {
    // Viewport-locked overlay sized to the full viewport so children with
    // width/height: 100% fill the screen. vw/dvh ignore CSS-transform scale
    // applied by ancestors, so the OUTER box needs no scale compensation.
    // `100dvh` (dynamic viewport, iOS 15.4+) tracks the *visible* height as
    // the Safari toolbar shows/hides — `100vh` is the LARGE viewport, taller
    // than visible, which pushes a bottom CTA under the toolbar and out of
    // reach.
    const fullscreenHeight = CSS.supports('height', '100dvh') ? '100dvh' : '100vh'

    applyContentStyles({
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: fullscreenHeight,
      'box-sizing': 'border-box',
      'z-index': String(zIndex.value),
    })

    // The INNER slot content, unlike the outer box, does need the same
    // compensation every other mode gets: inflate it by 1/scale, then scale
    // it back down from its top inline-start corner (top-left, top-right in RTL) —
    // it still lands exactly on the real viewport, but everything the author
    // declared in px now renders at the same size it would inside #app.
    //
    // `display` defaults to 'flow-root' (its scoped-CSS default is `contents`,
    // which has no box to transform) — `layoutStyle` overrides it to 'flex'
    // when the author picked a row/column layout.
    applyInnerStyles({
      display: 'flow-root',
      width: needsScale ? `${100 / scale}vw` : '100%',
      height: needsScale ? `calc(${fullscreenHeight} / ${scale})` : '100%',
      'box-sizing': 'border-box',
      'overflow-x': 'hidden',
      'overflow-y': 'auto',
      'overscroll-behavior': 'contain',
      ...(needsScale
        ? {
            transform: `scale(${scale})`,
            'transform-origin': isRtl.value ? 'top right' : 'top left',
          }
        : {}),
      ...layoutStyle.value,
    })
    return
  }

  // Non-fullscreen modes scale `.stick-content` itself (below), whose direct
  // children need to stay direct flex items when `layoutStyle` sets
  // `display: flex` on it — so the inner wrapper reverts to its scoped-CSS
  // default (`display: contents`, invisible to box generation) by clearing any
  // styles a prior 'fullscreen' pass left on it (an author can switch anytime).
  applyInnerStyles({})

  // Resolve the user-configured edge offset into viewport pixels by reading
  // the sentinel div (it lives inside the scaled ancestor chain, so its
  // viewportRect height is in the same viewport-pixel space as
  // placeholderRect).
  const offsetPx = offsetMeasureRef.value ? viewportRect(offsetMeasureRef.value).height : 0
  const anchor = position.value

  // Zoom the teleport target already applies: 1 for body, but a fix-to-bounds
  // target can sit inside the zoomed page wrapper (a SplitBackground section
  // before its own teleport). The content is scaled only by what is still
  // missing, and viewport px written into the target are divided by its zoom.
  const targetZoom = effectiveMode.value === 'fix-to-bounds' ? effectiveZoom(boundsEl.value) : 1
  const contentScale = scale / targetZoom

  // Both modes preserve the placeholder's horizontal position and width so
  // the overlay visually aligns with where the StickyContainer naturally
  // sits in the layout.
  const styles: Record<string, string> = {
    [anchor]: `${offsetPx / targetZoom}px`,
    width: `${offsetWidth}px`,
    'z-index': String(zIndex.value),
    ...layoutStyle.value,
    // Spread after the mirrored `width` above so an explicit width field
    // overrides the placeholder-matched default; left/scale are still
    // computed from the measured placeholder, so a custom width may drift
    // out of horizontal alignment — the author's call, same as offset.
    ...sizeStyle.value,
  }
  if (Math.abs(contentScale - 1) > 0.005) {
    styles.transform = `scale(${contentScale})`
    styles['transform-origin'] = anchor === 'top' ? 'top left' : 'bottom left'
  }

  if (effectiveMode.value === 'fix-to-bounds') {
    // Teleported into boundsEl itself (see the Teleport `:to` binding below),
    // not body. Rides the screen edge while that edge is inside boundsEl, and
    // stops at boundsEl's own edge once the visitor scrolls past it — so a
    // bottom CTA stays on screen over long section content, but never overlaps
    // the next section (e.g. a footer in SplitBackground's bottom half).
    const boundsRect = viewportRect(boundsEl.value!)
    const contentHeight = viewportRect(content).height
    // Distance from the anchored edge of boundsEl to the matching screen edge,
    // in viewport px: positive while boundsEl extends past the screen.
    const overshoot =
      anchor === 'top' ? -boundsRect.top : boundsRect.top + boundsRect.height - window.innerHeight
    // How far the content may travel from boundsEl's anchored edge before it
    // would leave boundsEl through the opposite edge.
    const maxInset = Math.max(offsetPx, boundsRect.height - contentHeight)
    const inset = Math.min(Math.max(offsetPx, overshoot + offsetPx), maxInset)

    if (inset === overshoot + offsetPx) {
      // On the screen edge: `fixed` follows the scroll natively, so the content
      // does not lag a frame behind on every scroll event the way a
      // per-scroll `absolute` reposition would. boundsEl and its ancestors set
      // no transform, so the viewport is still the containing block.
      styles.position = 'fixed'
      styles.left = `${placeholderRect.left / targetZoom}px`
    } else {
      // Clamped to boundsEl's edge: its own box, already `position: relative`
      // (SplitBackground's .split-bg__top/__bottom), is the containing block.
      styles.position = 'absolute'
      styles[anchor] = `${inset / targetZoom}px`
      styles.left = `${(placeholderRect.left - boundsRect.left) / targetZoom}px`
    }
  } else if (effectiveMode.value === 'fix-to-page') {
    // Body must be `position: relative` so it becomes the containing block —
    // otherwise the initial containing block (viewport) is used and
    // `bottom: 0` sits at the viewport bottom, not the page bottom, when
    // content overflows. Guarded re-assert: self-heals if another component
    // (e.g. SplitBackground) cleared it on unmount, without invalidating
    // style on every pass.
    if (document.body.style.position !== 'relative') {
      document.body.style.position = 'relative'
    }
    styles.position = 'absolute'
    // Document-x = viewport-x + scrollX (so horizontal scroll doesn't shift it).
    styles.left = `${placeholderRect.left + window.scrollX}px`
  } else {
    styles.position = 'fixed'
    styles.left = `${placeholderRect.left}px`
  }

  applyContentStyles(styles)
}

let rafId: number | null = null
const scheduleRecalc = () => {
  if (rafId !== null) return
  rafId = requestAnimationFrame(() => {
    rafId = null
    recalc()
  })
}

let resizeObserver: ResizeObserver | null = null

const startOverlay = () => {
  if (overlayActive) return
  overlayActive = true
  window.addEventListener('scroll', scheduleRecalc, { passive: true })
  window.addEventListener('resize', scheduleRecalc, { passive: true })
  resizeObserver = new ResizeObserver(scheduleRecalc)
  if (placeholderRef.value) resizeObserver.observe(placeholderRef.value)
  if (contentRef.value) resizeObserver.observe(contentRef.value)
  if (boundsEl.value) resizeObserver.observe(boundsEl.value)
  nextTick(() => {
    // Position first, then enable the teleport: Vue batches both into the
    // same render pass, so the moment the content lands in body it is
    // already fixed/absolute where it belongs.
    recalc()
    const phases = captureAnimationPhases(contentRef.value)
    overlayMounted.value = true
    nextTick(() => restoreAnimationPhases(phases))
  })
}

const stopOverlay = () => {
  if (!overlayActive) return
  overlayActive = false
  window.removeEventListener('scroll', scheduleRecalc)
  window.removeEventListener('resize', scheduleRecalc)
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }
  resizeObserver?.disconnect()
  resizeObserver = null
  appliedStyles = {}
  appliedInnerStyles = {}
  overlayMounted.value = false
  placeholderHeight.value = '0px'
}

let holdsBodyRelative = false
const syncBodyRelative = () => {
  const needs = overlayActive && effectiveMode.value === 'fix-to-page'
  if (needs === holdsBodyRelative) return
  holdsBodyRelative = needs
  if (needs) {
    fixToPageHolders++
    document.body.style.position = 'relative'
  } else {
    fixToPageHolders = Math.max(0, fixToPageHolders - 1)
    if (fixToPageHolders === 0) document.body.style.position = ''
  }
}

onMounted(() => {
  if (!isNativeSticky.value) {
    startOverlay()
    syncBodyRelative()
  }
})

onBeforeUnmount(() => {
  stopOverlay()
  syncBodyRelative()
})

// Field edits (editor): re-position, and start/stop the overlay machinery
// when the mode crosses the native↔overlay boundary. nextTick because the
// v-if swaps template branches, so the refs only exist after the re-render.
watch(
  [
    mode,
    position,
    zIndex,
    offsetCss,
    devHeadingPadding,
    () => fields.value.reserveSpace,
    layoutStyle,
    sizeStyle,
    isRtl,
  ],
  () => {
    nextTick(() => {
      if (isNativeSticky.value) {
        stopOverlay()
      } else {
        startOverlay()
        recalc()
      }
      syncBodyRelative()
    })
  },
)
</script>

<template>
  <div v-if="isNativeSticky" class="stick-native" :style="nativeStickyStyle">
    <slot></slot>
  </div>

  <div
    v-else
    ref="placeholderRef"
    class="stick-placeholder"
    :style="{ height: placeholderHeight }"
    data-allow-mismatch="style"
  >
    <div
      ref="offsetMeasureRef"
      aria-hidden="true"
      class="stick-offset-measure"
      :style="{ height: offsetCss }"
    ></div>
    <Teleport :to="teleportTarget" :disabled="!overlayMounted">
      <div
        ref="contentRef"
        class="stick-content"
        :style="prerendered?.content"
        :data-teleport-owner="teleportOwner"
        data-allow-mismatch="style"
      >
        <div
          ref="innerRef"
          class="stick-content-inner"
          :style="prerendered?.inner"
          data-allow-mismatch="style"
        >
          <slot></slot>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.stick-native {
  position: sticky;
  display: flow-root;
  width: 100%;
  padding: v-bind(devHeadingPadding);
}

.stick-placeholder {
  display: block;
  width: 100%;
}

.stick-offset-measure {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  width: 0;
}

.stick-content {
  /* No `will-change` here: it forces iOS WebKit to keep the body-teleported
     fixed/absolute overlay on a persistent compositing layer whose touch
     hit-region de-syncs from its painted box, swallowing taps on unrelated
     content. Positions update discretely on scroll/resize (not per-frame), so
     the hint bought nothing. See SplitBackground.vue — same body teleport,
     no will-change, no tap interference. */
  display: flow-root;
  width: 100%;
  padding: v-bind(devHeadingPadding);
}

.stick-content-inner {
  /* `display: contents` for every mode except 'fullscreen': it generates no
     box of its own, so its slot children stay direct children of
     `.stick-content` in the box tree — required for `layoutStyle`'s
     `display: flex` on `.stick-content` to arrange them as flex items instead
     of one opaque block. `.stick-content` itself already carries the design-
     width scale compensation for these modes, so this wrapper needs no box.
     'fullscreen' is the exception: it overrides display/width/height/transform
     imperatively in recalc() — see the comment there — because a `contents`
     box has nothing for `transform` to scale. */
  display: contents;
}
</style>
